import type { SettingsData } from './persistence';
import type { SimEvent } from '../sim/state';
import type { Vec2 } from '../shared/vec';
import type { PlayerId, TeamId } from '../content/types';
import { MusicHandler, type MusicScene } from './music';
import { SFX_ASSET_URLS, SFX_MANIFEST, type GlobalCue, type OrderCue, type SfxCue, type UiCue, type WorldCue } from './sfxManifest';

export type { GlobalCue, OrderCue, UiCue, WorldCue } from './sfxManifest';

interface SpatialSound {
  pos: Vec2;
  listener: Vec2;
  maxDistance: number;
}

interface QueuedWorldSound {
  cue: WorldCue;
  pos: Vec2;
}

export type EventPositionResolver = (event: SimEvent) => Vec2 | null;

const WORLD_RADIUS: Record<WorldCue, number> = {
  capture: 95,
  boss: 135,
  projectile: 62,
  melee: 52,
  heavyImpact: 92,
  collapse: 105,
  death: 58,
  construction: 72,
  gateOpen: 78,
  gateClose: 78,
  upgrade: 84,
  ability: 82,
  trap: 78,
  raid: 120,
  teleport: 85,
  spawn: 58,
  ritualStart: 92,
  ritualComplete: 110,
  ritualCancel: 78,
};

const MIN_GAP: Partial<Record<UiCue | OrderCue | WorldCue | GlobalCue, number>> = {
  hover: 0.035,
  // Card hover can fire repeatedly while crossing the hand. Keep the short shuffle from
  // stacking into a continuous rustle.
  cardHover: 0.34,
  projectile: 0.045,
  melee: 0.065,
  heavyImpact: 0.08,
  death: 0.09,
  spawn: 0.08,
  ability: 0.07,
  gateOpen: 0.22,
  gateClose: 0.22,
  // CoreDamaged may be emitted by several attackers on adjacent ticks. This cue is a
  // long two-note alarm, so keep it from layering into a continuous siren.
  raid: 1.6,
};

// Procedural fallback effects are intentionally authored at a conservative oscillator level.
// Boost the effects bus at the source so fallback clicks, impacts, and battlefield cues can
// compete with mastered music without requiring users to max out the effects slider.
const SFX_GAIN_BOOST = 3.25;

function distanceSq(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function embeddedPosition(event: SimEvent): Vec2 | null {
  if (!('pos' in event)) return null;
  const pos = event.pos;
  return pos && typeof pos.x === 'number' && typeof pos.y === 'number' ? pos : null;
}

/**
 * Event-driven Web Audio presentation layer.
 *
 * UI feedback is intentionally non-spatial. Battlefield sounds are attenuated and panned from
 * the camera focus so a fight on the other side of the map no longer sounds as if it is under the
 * cursor. Curated local recordings provide the primary sound families; the synthesized cue map
 * remains available as a resilient fallback while assets load or if a request fails.
 */
export class AudioDirector {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private samples = new Map<string, AudioBuffer>();
  private sampleLoadPromise: Promise<void> | null = null;
  private lastSample = new Map<SfxCue, string>();
  private music: MusicHandler;
  private settings: SettingsData;
  private disposed = false;
  private lastPlayed = new Map<string, number>();

  constructor(settings: SettingsData) {
    this.settings = settings;
    this.music = new MusicHandler(settings);
    window.addEventListener('pointerdown', this.unlock, { once: true });
    window.addEventListener('keydown', this.unlock, { once: true });
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  setSettings(settings: SettingsData): void {
    this.settings = settings;
    this.updateGain();
    this.music.setSettings(settings);
  }

  setMusicScene(scene: MusicScene): void {
    this.music.setScene(scene);
  }

  setBaseThreat(active: boolean): void {
    this.music.setBaseThreat(active);
  }

  ui(cue: UiCue): void {
    this.playUi(cue);
  }

  order(cue: OrderCue, pos: Vec2 | null, listener: Vec2): void {
    if (!pos) {
      this.playUi(cue === 'attack' ? 'confirm' : 'select');
      return;
    }
    const worldCue: WorldCue = cue === 'attack'
      ? 'heavyImpact'
      : cue === 'capture'
        ? 'capture'
        : cue === 'mount'
          ? 'construction'
          : cue === 'repair' || cue === 'rally'
            ? 'upgrade'
            : 'spawn';
    this.playOrder(cue, { pos, listener, maxDistance: WORLD_RADIUS[worldCue] });
  }

  events(
    events: SimEvent[],
    localPlayer: PlayerId,
    localTeam: TeamId,
    listener: Vec2,
    resolvePosition: EventPositionResolver = embeddedPosition,
  ): void {
    const world: QueuedWorldSound[] = [];
    const at = (event: SimEvent): Vec2 | null => embeddedPosition(event) ?? resolvePosition(event);
    const queue = (cue: WorldCue, event: SimEvent): void => {
      const pos = at(event);
      if (pos) world.push({ cue, pos });
    };

    for (const event of events) {
      switch (event.type) {
        case 'CardPlayed':
          if (event.player === localPlayer) this.playUi('confirm');
          break;
        case 'CardDenied':
          if (event.player === localPlayer) this.playUi('deny');
          break;
        case 'SiteCaptured':
        case 'SiteNeutralized':
          queue('capture', event);
          break;
        case 'SiteLost':
          if (event.loser === localPlayer) queue('raid', event);
          break;
        case 'AscensionUnlocked':
          if (event.player === localPlayer) this.playGlobal('ascend');
          break;
        case 'BossSummoning':
        case 'BossSummoned':
          queue('boss', event);
          break;
        case 'BarrageImpact':
        case 'GiantCrush':
          queue('heavyImpact', event);
          break;
        case 'ProjectileHit':
          queue('projectile', event);
          break;
        case 'MeleeImpact':
          queue(event.weight === 'light' && !event.structure ? 'melee' : 'heavyImpact', event);
          break;
        case 'BuildingDestroyed':
          queue('collapse', event);
          break;
        case 'SquadDied':
        case 'WorkerDied':
          queue('death', event);
          break;
        case 'BuildingComplete':
          queue('construction', event);
          break;
        case 'GateOpened':
          queue('gateOpen', event);
          break;
        case 'GateClosed':
          queue('gateClose', event);
          break;
        case 'UpgradeComplete':
          queue('upgrade', event);
          break;
        case 'AbilityTriggered':
          queue('ability', event);
          break;
        case 'TrapTriggered':
          queue('trap', event);
          break;
        case 'RaidWarning':
          if (event.player === localPlayer) queue('raid', event);
          break;
        case 'CoreDamaged':
          if (event.player === localPlayer) queue('raid', event);
          break;
        case 'SquadTeleported':
          world.push({ cue: 'teleport', pos: event.to });
          break;
        case 'SquadSpawned':
          queue('spawn', event);
          break;
        case 'RitualStarted':
          queue('ritualStart', event);
          break;
        case 'RitualComplete':
          queue('ritualComplete', event);
          break;
        case 'RitualCancelled':
          queue('ritualCancel', event);
          break;
        case 'MatchEnded':
          this.playGlobal(event.winningTeam === localTeam ? 'victory' : 'defeat');
          break;
        default:
          break;
      }
    }

    // When a tick contains many hits, let the closest audible event win each rate-limited cue.
    world.sort((a, b) => distanceSq(a.pos, listener) - distanceSq(b.pos, listener));
    for (const sound of world) {
      this.playWorld(sound.cue, {
        pos: sound.pos,
        listener,
        maxDistance: WORLD_RADIUS[sound.cue],
      });
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener('pointerdown', this.unlock);
    window.removeEventListener('keydown', this.unlock);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.music.dispose();
    void this.context?.close();
    this.context = null;
    this.master = null;
    this.noise = null;
    this.samples.clear();
    this.sampleLoadPromise = null;
    this.lastSample.clear();
  }

  private unlock = (): void => {
    if (this.disposed) return;
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.connect(this.context.destination);
      this.noise = this.makeNoiseBuffer(this.context);
      this.music.attach(this.context);
      this.updateGain();
      this.sampleLoadPromise = this.loadSamples(this.context);
    }
    if (this.context.state === 'suspended') void this.context.resume();
  };

  private onVisibility = (): void => {
    if (!this.context) return;
    if (document.hidden && this.settings.muteWhenMinimized) void this.context.suspend();
    else if (!document.hidden) void this.context.resume();
  };

  private updateGain(): void {
    if (!this.master || !this.context) return;
    const volume = (this.settings.masterVol / 100) * (this.settings.sfxVol / 100);
    this.master.gain.setTargetAtTime(volume, this.context.currentTime, 0.02);
  }

  private ready(cue: string, spatial?: SpatialSound): { context: AudioContext; output: GainNode } | null {
    const context = this.context;
    const output = this.master;
    if (
      !context ||
      !output ||
      context.state !== 'running' ||
      (document.hidden && this.settings.muteWhenMinimized) ||
      this.settings.masterVol === 0 ||
      this.settings.sfxVol === 0
    ) return null;
    // An off-camera event must not consume the cue cooldown and silence an audible event
    // that follows on the next simulation tick.
    if (spatial && distanceSq(spatial.pos, spatial.listener) >= spatial.maxDistance ** 2) return null;
    const last = this.lastPlayed.get(cue) ?? -Infinity;
    const gap = MIN_GAP[cue as keyof typeof MIN_GAP] ?? 0.025;
    if (context.currentTime - last < gap) return null;
    this.lastPlayed.set(cue, context.currentTime);
    return { context, output };
  }

  private makeBus(context: AudioContext, output: GainNode, spatial?: SpatialSound): GainNode | null {
    const bus = context.createGain();
    if (!spatial) {
      bus.connect(output);
      window.setTimeout(() => bus.disconnect(), 5000);
      return bus;
    }
    const distance = Math.sqrt(distanceSq(spatial.pos, spatial.listener));
    if (distance >= spatial.maxDistance) {
      bus.disconnect();
      return null;
    }
    const near = 8;
    const normalized = Math.max(0, Math.min(1, (distance - near) / Math.max(1, spatial.maxDistance - near)));
    bus.gain.value = Math.pow(1 - normalized, 1.65);
    const pan = Math.max(-0.78, Math.min(0.78, (spatial.pos.x - spatial.listener.x) / (spatial.maxDistance * 0.42)));
    const panner = context.createStereoPanner();
    panner.pan.value = pan;
    bus.connect(panner);
    panner.connect(output);
    window.setTimeout(() => {
      bus.disconnect();
      panner.disconnect();
    }, 5000);
    return bus;
  }

  private tone(
    context: AudioContext,
    bus: AudioNode,
    start: number,
    duration: number,
    frequency: number,
    gainValue: number,
    type: OscillatorType = 'sine',
    endFrequency = frequency,
  ): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(25, frequency), start);
    if (endFrequency !== frequency) {
      oscillator.frequency.exponentialRampToValueAtTime(Math.max(25, endFrequency), start + duration);
    }
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, gainValue * SFX_GAIN_BOOST), start + Math.min(0.014, duration * 0.2));
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain);
    gain.connect(bus);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.015);
  }

  private noiseBurst(
    context: AudioContext,
    bus: AudioNode,
    start: number,
    duration: number,
    gainValue: number,
    filterType: BiquadFilterType,
    frequency: number,
    q = 0.8,
  ): void {
    if (!this.noise) return;
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = this.noise;
    source.loop = duration > this.noise.duration;
    filter.type = filterType;
    filter.frequency.value = frequency;
    filter.Q.value = q;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, gainValue * SFX_GAIN_BOOST), start + Math.min(0.01, duration * 0.18));
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(bus);
    source.start(start, 0, Math.min(duration + 0.02, this.noise.duration));
  }

  private async loadSamples(context: AudioContext): Promise<void> {
    const loaded = await Promise.allSettled(SFX_ASSET_URLS.map(async (url) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`SFX request failed: ${response.status} ${url}`);
      const data = await response.arrayBuffer();
      return [url, await context.decodeAudioData(data)] as const;
    }));

    if (this.disposed || this.context !== context) return;
    for (const result of loaded) {
      if (result.status === 'fulfilled') this.samples.set(result.value[0], result.value[1]);
    }
  }

  /** Play a decoded recording when available; the caller keeps its procedural fallback below. */
  private playSample(
    cue: SfxCue,
    bus: GainNode,
    start: number,
    gainValue: number,
    maxDuration = Number.POSITIVE_INFINITY,
  ): boolean {
    const choices = SFX_MANIFEST[cue].filter((url) => this.samples.has(url));
    if (choices.length === 0) return false;

    const previous = this.lastSample.get(cue);
    const withoutPrevious = choices.length > 1 ? choices.filter((url) => url !== previous) : choices;
    const url = withoutPrevious[Math.floor(Math.random() * withoutPrevious.length)] ?? choices[0];
    const buffer = this.samples.get(url);
    if (!buffer) return false;
    this.lastSample.set(cue, url);

    const source = this.context?.createBufferSource();
    const gain = this.context?.createGain();
    if (!source || !gain || !this.context) return false;
    const duration = Math.max(0.025, Math.min(buffer.duration, maxDuration));
    source.buffer = buffer;
    source.playbackRate.value = 0.96 + Math.random() * 0.08;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, gainValue), start + Math.min(0.012, duration * 0.18));
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.connect(gain);
    gain.connect(bus);
    source.start(start);
    source.stop(start + duration + 0.02);
    window.setTimeout(() => {
      source.disconnect();
      gain.disconnect();
    }, Math.ceil((duration + 0.1) * 1000));
    return true;
  }

  private playUi(cue: UiCue): void {
    const ready = this.ready(cue);
    if (!ready) return;
    const { context, output } = ready;
    const bus = this.makeBus(context, output);
    if (!bus) return;
    const t = context.currentTime + 0.004;
    const sampleGain = cue === 'cardHover' ? 0.3 : cue === 'select' ? 0.24 : 0.62;
    const sampleMaxDuration = cue === 'cardHover' ? 0.32 : Number.POSITIVE_INFINITY;
    if (this.playSample(cue, bus, t, sampleGain, sampleMaxDuration)) return;
    switch (cue) {
      case 'hover':
        this.tone(context, bus, t, 0.045, 620, 0.025, 'sine', 760);
        break;
      case 'click':
        this.noiseBurst(context, bus, t, 0.035, 0.022, 'highpass', 1800);
        this.tone(context, bus, t, 0.065, 310, 0.038, 'triangle', 250);
        break;
      case 'cardHover':
        // Three papery flicks read as a quick hand shuffle without requiring recorded assets.
        this.noiseBurst(context, bus, t, 0.1, 0.032, 'bandpass', 2300, 0.65);
        this.tone(context, bus, t, 0.035, 1180, 0.018, 'triangle', 850);
        this.tone(context, bus, t + 0.025, 0.035, 980, 0.016, 'triangle', 720);
        this.tone(context, bus, t + 0.05, 0.04, 1260, 0.015, 'triangle', 900);
        break;
      case 'cardPick':
        this.noiseBurst(context, bus, t, 0.055, 0.028, 'highpass', 1500);
        this.tone(context, bus, t, 0.1, 360, 0.045, 'triangle', 520);
        break;
      case 'select':
        this.tone(context, bus, t, 0.07, 470, 0.038, 'sine', 610);
        this.tone(context, bus, t + 0.025, 0.065, 690, 0.025, 'sine', 760);
        break;
      case 'confirm':
        this.tone(context, bus, t, 0.13, 420, 0.055, 'triangle', 520);
        this.tone(context, bus, t + 0.055, 0.14, 630, 0.052, 'triangle', 790);
        break;
      case 'deny':
        this.tone(context, bus, t, 0.17, 190, 0.055, 'sawtooth', 125);
        this.tone(context, bus, t + 0.055, 0.15, 145, 0.042, 'square', 105);
        break;
      case 'open':
        this.noiseBurst(context, bus, t, 0.16, 0.025, 'bandpass', 1000);
        this.tone(context, bus, t, 0.18, 260, 0.045, 'triangle', 520);
        break;
      case 'close':
        this.tone(context, bus, t, 0.14, 520, 0.042, 'triangle', 230);
        break;
    }
  }

  private playOrder(cue: OrderCue, spatial: SpatialSound): void {
    const ready = this.ready(`order:${cue}`, spatial);
    if (!ready) return;
    const { context, output } = ready;
    const bus = this.makeBus(context, output, spatial);
    if (!bus) return;
    const t = context.currentTime + 0.004;
    if (this.playSample(cue, bus, t, cue === 'attack' ? 0.68 : 0.58)) return;
    switch (cue) {
      case 'attack':
        this.noiseBurst(context, bus, t, 0.08, 0.045, 'lowpass', 620);
        this.tone(context, bus, t, 0.13, 155, 0.06, 'sawtooth', 82);
        break;
      case 'capture':
        this.tone(context, bus, t, 0.12, 330, 0.045, 'triangle', 440);
        this.tone(context, bus, t + 0.06, 0.13, 495, 0.04, 'triangle', 590);
        break;
      case 'mount':
      case 'repair':
        this.noiseBurst(context, bus, t, 0.1, 0.04, 'bandpass', 1200, 2.5);
        this.tone(context, bus, t, 0.11, 720, 0.035, 'square', 540);
        break;
      case 'defend':
      case 'rally':
        this.tone(context, bus, t, 0.14, 285, 0.045, 'triangle', 430);
        break;
      case 'withdraw':
        this.tone(context, bus, t, 0.16, 430, 0.045, 'triangle', 185);
        break;
      case 'move':
      default:
        this.noiseBurst(context, bus, t, 0.045, 0.018, 'highpass', 1600);
        this.tone(context, bus, t, 0.095, 250, 0.042, 'triangle', 380);
        break;
    }
  }

  private playWorld(cue: WorldCue, spatial: SpatialSound): void {
    const ready = this.ready(cue, spatial);
    if (!ready) return;
    const { context, output } = ready;
    const bus = this.makeBus(context, output, spatial);
    if (!bus) return;
    const t = context.currentTime + 0.006;
    if (this.playSample(cue, bus, t, cue === 'projectile' || cue === 'trap' ? 0.46 : 0.62)) return;
    switch (cue) {
      case 'projectile':
        this.noiseBurst(context, bus, t, 0.075, 0.045, 'highpass', 900);
        this.tone(context, bus, t, 0.085, 145, 0.04, 'square', 72);
        break;
      case 'melee':
        this.noiseBurst(context, bus, t, 0.09, 0.052, 'bandpass', 720, 1.4);
        this.tone(context, bus, t, 0.08, 190, 0.038, 'triangle', 105);
        break;
      case 'heavyImpact':
        this.noiseBurst(context, bus, t, 0.24, 0.075, 'lowpass', 430);
        this.tone(context, bus, t, 0.3, 92, 0.085, 'sawtooth', 38);
        break;
      case 'collapse':
        this.noiseBurst(context, bus, t, 0.52, 0.085, 'lowpass', 820);
        this.tone(context, bus, t, 0.48, 105, 0.075, 'sawtooth', 34);
        break;
      case 'death':
        this.noiseBurst(context, bus, t, 0.16, 0.045, 'bandpass', 520);
        this.tone(context, bus, t, 0.18, 175, 0.035, 'triangle', 72);
        break;
      case 'construction':
        this.noiseBurst(context, bus, t, 0.16, 0.042, 'bandpass', 1450, 2.8);
        this.tone(context, bus, t, 0.18, 360, 0.04, 'square', 540);
        break;
      case 'gateOpen':
        // Low stone/metal travel followed by a light latch release. The cue is positional,
        // so a remote gate never sounds as though it opened under the camera.
        this.noiseBurst(context, bus, t, 0.34, 0.046, 'lowpass', 520);
        this.tone(context, bus, t, 0.33, 92, 0.046, 'sawtooth', 138);
        this.tone(context, bus, t + 0.24, 0.09, 410, 0.026, 'triangle', 560);
        break;
      case 'gateClose':
        this.noiseBurst(context, bus, t, 0.31, 0.048, 'lowpass', 470);
        this.tone(context, bus, t, 0.3, 132, 0.044, 'sawtooth', 76);
        this.noiseBurst(context, bus, t + 0.24, 0.1, 0.056, 'bandpass', 780, 1.8);
        break;
      case 'upgrade':
        this.tone(context, bus, t, 0.17, 330, 0.045, 'triangle', 495);
        this.tone(context, bus, t + 0.08, 0.2, 495, 0.04, 'triangle', 740);
        break;
      case 'ability':
        this.noiseBurst(context, bus, t, 0.22, 0.04, 'bandpass', 1250, 0.7);
        this.tone(context, bus, t, 0.28, 240, 0.05, 'sine', 890);
        break;
      case 'trap':
        this.noiseBurst(context, bus, t, 0.17, 0.062, 'highpass', 760);
        this.tone(context, bus, t, 0.2, 250, 0.05, 'square', 65);
        break;
      case 'raid':
        this.tone(context, bus, t, 0.34, 196, 0.06, 'sawtooth', 174);
        this.tone(context, bus, t + 0.18, 0.36, 147, 0.055, 'sawtooth', 122);
        break;
      case 'teleport':
        this.noiseBurst(context, bus, t, 0.24, 0.035, 'highpass', 1850);
        this.tone(context, bus, t, 0.3, 880, 0.045, 'sine', 210);
        break;
      case 'spawn':
        this.tone(context, bus, t, 0.13, 220, 0.035, 'triangle', 350);
        break;
      case 'capture':
        this.tone(context, bus, t, 0.28, 294, 0.05, 'triangle', 392);
        this.tone(context, bus, t + 0.1, 0.31, 440, 0.05, 'triangle', 587);
        this.tone(context, bus, t + 0.2, 0.34, 587, 0.045, 'triangle', 784);
        break;
      case 'boss':
        this.noiseBurst(context, bus, t, 0.65, 0.07, 'lowpass', 310);
        this.tone(context, bus, t, 0.75, 92, 0.095, 'sawtooth', 42);
        this.tone(context, bus, t + 0.18, 0.62, 138, 0.055, 'square', 69);
        break;
      case 'ritualStart':
        this.tone(context, bus, t, 0.42, 185, 0.045, 'sine', 555);
        this.noiseBurst(context, bus, t, 0.38, 0.03, 'bandpass', 980, 4);
        break;
      case 'ritualComplete':
        this.tone(context, bus, t, 0.55, 262, 0.055, 'sine', 784);
        this.tone(context, bus, t + 0.13, 0.5, 392, 0.045, 'triangle', 1047);
        break;
      case 'ritualCancel':
        this.tone(context, bus, t, 0.28, 430, 0.045, 'sine', 110);
        break;
    }
  }

  private playGlobal(cue: GlobalCue): void {
    const ready = this.ready(cue);
    if (!ready) return;
    const { context, output } = ready;
    const bus = this.makeBus(context, output);
    if (!bus) return;
    const t = context.currentTime + 0.01;
    if (this.playSample(cue, bus, t, 0.62)) return;
    const notes = cue === 'victory'
      ? [262, 330, 392, 523]
      : cue === 'defeat'
        ? [220, 185, 147, 110]
        : [261, 392, 523, 784];
    notes.forEach((frequency, index) => {
      this.tone(
        context,
        bus,
        t + index * (cue === 'ascend' ? 0.11 : 0.18),
        cue === 'ascend' ? 0.55 : 0.72,
        frequency,
        cue === 'defeat' ? 0.06 : 0.07,
        cue === 'defeat' ? 'sine' : 'triangle',
      );
    });
    if (cue === 'ascend') this.noiseBurst(context, bus, t, 0.7, 0.035, 'highpass', 1100);
  }

  private makeNoiseBuffer(context: AudioContext): AudioBuffer {
    const length = Math.ceil(context.sampleRate * 1.25);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i++) samples[i] = Math.random() * 2 - 1;
    return buffer;
  }
}
